/**
 * @file element-warnings.ts
 * @description Предупреждения об элементах секции словами владельца
 * (phase19.16, партия SB120, этап 3а; uiux-sb120-stage3 §2–§4, §7; данные
 * db-design-sb120-stage3 §6 — `sectionElementWarnings` из shared). Этап 3б:
 * «7–8 в ряд», «светлый текст без затемнения», «смена быстрее 5 с».
 * Не запреты: «!» / «✕» у группы и строка в списке замечаний секции.
 *
 * Свой текст на каждый код — с числом (контраст, px, число шрифтов); без
 * числа — общий текст shared (`SECTION_ELEMENT_WARNING_MESSAGES`). Реестр
 * по коду: новый код shared без записи здесь — ошибка tsc.
 */

import {
  SECTION_ELEMENT_WARNING_MESSAGES,
  type SectionElementWarning,
  type SectionElementWarningCode,
} from "@chaiberi/shared";

import { formatNumber } from "../site-fields/number-input";

type WarningText = (value: number | undefined) => string | null;

const TEXT: Readonly<Record<SectionElementWarningCode, WarningText>> = {
  text_contrast_low: (v) => (v === undefined ? null : `Контраст ${formatNumber(v, 1)}:1 — читается только крупный текст.`),
  text_contrast_very_low: (v) => (v === undefined ? null : `Контраст ${formatNumber(v, 1)}:1 — текст почти не виден.`),
  button_height_lt48: (v) => (v === undefined ? null : `Кнопка ${formatNumber(v, 2)} px — ниже 48 px пальцем трудно попасть.`),
  fonts_over_3: (v) => (v === undefined ? null : `Шрифтов на странице: ${v} — загрузка медленнее. Лучше до 3.`),
  h1_small: (v) => (v === undefined ? null : `Главный заголовок ${formatNumber(v, 2)} px — мельче обычного, теряется на странице.`),
  overlay_no_plate: () => null,
  element_font_missing: () => null,
  // SB120 этап 3б (uiux-sb120-stage3 §3, §7).
  grid_cols_many: (v) => (v === undefined ? null : `${v} в ряд — на 1280 плитка ≈ 140 px, подпись не влезет.`),
  tile_light_no_shade: () => null,
  announcement_fast: (v) => (v === undefined ? null : `Смена каждые ${v} с — объявление не успеют дочитать.`),
};

/** Текст предупреждения об элементе. */
export function elementWarningText(warning: SectionElementWarning): string {
  return TEXT[warning.code](warning.value) ?? SECTION_ELEMENT_WARNING_MESSAGES[warning.code];
}

const isUnder = (path: string, prefix: string): boolean => path === prefix || path.startsWith(`${prefix}.`);

/** Предупреждения под путём (поле или часть). */
export function elementWarningsUnder(warnings: readonly SectionElementWarning[], prefix: string): SectionElementWarning[] {
  return warnings.filter((w) => isUnder(w.path, prefix));
}

/** Предупреждения ровно по пути поля (`….cols`, `….shade`) — под самим полем. */
export function elementWarningsAt(warnings: readonly SectionElementWarning[], path: string): SectionElementWarning[] {
  return warnings.filter((w) => w.path === path);
}

/** Сколько семейств будет на странице (из предупреждения «больше 3»); нет — `undefined`. */
export function familiesOnPage(warnings: readonly SectionElementWarning[]): number | undefined {
  return warnings.find((w) => w.code === "fonts_over_3")?.value;
}
